import { APP_BASE_HREF } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  enableProdMode,
  ErrorHandler,
  inject,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  NavigationError,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../../environments/environment';
import { authenticationInterceptor } from '../common/interceptors/authentication.interceptor';
import { captchaTokenInterceptor } from '../common/services/captcha/captcha-token.interceptor';
import { loadingInterceptor } from '../common/interceptors/loading.interceptor';
import { LogFactory } from '../common/services/logger/log-factory';
import { installToastReadingTime } from '../common/services/toast/toast-reading-time';
import { installTableWheelScroll } from './core/table-scroll/table-wheel-scroll';
import { PlatformService } from '../common/services/platform/platform.service';
import { routes } from './app.routes';
import { AdminPreset } from './admin-theme.preset';
import { GlobalErrorHandlerService } from './core/error-handler/global-error-handler.service';
import { httpErrorInterceptor } from '../common/interceptors/http-error-interceptor/http-error.interceptor';
// eslint-disable-next-line local-rules/no-app-env-config-injection
import { APP_ENV_CONFIG } from "../common/config/app-config";

export function baseHrefFactory(): string {
  return '/';
}

export const baseHrefProvider = {
  provide: APP_BASE_HREF,
  useFactory: baseHrefFactory,
  deps: [] as never[],
};

// Enable prod mode if ENVIRONMENT is 'Production'
if (environment.app.production) {
  enableProdMode();
}

export const BOOTSTRAP_CONFIG: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    LogFactory,
    PlatformService,
    baseHrefProvider,
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation(),
      // A failed lazy-chunk import (e.g. tab left open across a deploy →
      // stale hashed chunk 404s) rejects the navigation Promise instead of
      // reaching Angular's ErrorHandler. Forward it so GlobalErrorHandler can
      // detect the stale build and surface the reload prompt.
      withNavigationErrorHandler((error: NavigationError) =>
        inject(GlobalErrorHandlerService).handleError(error.error),
      ),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authenticationInterceptor,
        // Carries the security-check token onto the credential request.
        captchaTokenInterceptor,
        httpErrorInterceptor,
        loadingInterceptor,
      ]),
    ),
    providePrimeNG({
      theme: { preset: AdminPreset, options: { darkModeSelector: '.app-dark' } },
    }),
      
    {
      provide: APP_ENV_CONFIG,
      useValue: environment, // Use the ADMIN app's concrete service
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    MessageService,
    ConfirmationService,
    // Every toast gets reading time - including those of screens that
    // provide their own MessageService.
    provideEnvironmentInitializer(installToastReadingTime),
    // The mouse wheel over a table wider than its card scrolls it sideways.
    provideEnvironmentInitializer(installTableWheelScroll),
  ],
};
